import { useSyncExternalStore } from 'react';
import { sessionDto, type CurrentAccountDto, type SessionDto } from '@ederest/contracts';

export type Role = 'platform_access' | 'central_access';

/**
 * - `unknown`: the page just loaded, the refresh cookie is being tried.
 * - `signed_in`: an access token is held in memory.
 * - `expired`: the session ended while the Console was open; the screen stays
 *   mounted under the sign-in dialog, so a draft in progress is not lost
 *   (functional §9.2).
 * - `signed_out`: nobody is signed in.
 */
export type SessionStatus = 'unknown' | 'signed_in' | 'expired' | 'signed_out';

export interface SessionState {
  readonly status: SessionStatus;
  /** Kept while `expired`, so the screen under the dialog still knows who it belonged to. */
  readonly account: CurrentAccountDto | null;
}

const AUTH_BASE = '/api/v1/auth';
/** Serialises renewals across tabs: two tabs exchanging the same refresh token would read as a theft. */
const REFRESH_LOCK = 'ederest-session-refresh';
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60_000;
/** Renew in the background once half of the access token's life is gone and the user is active. */
const PROACTIVE_RENEWAL_SHARE = 0.5;

let state: SessionState = { status: 'unknown', account: null };
let accessToken: string | null = null;
let tokenIssuedAt = 0;
let tokenLifetimeMs = 0;
let lastGestureAt = Date.now();
let renewal: Promise<boolean> | null = null;
const listeners = new Set<() => void>();

function setState(next: SessionState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

function adopt(session: SessionDto): void {
  accessToken = session.accessToken;
  tokenIssuedAt = Date.now();
  tokenLifetimeMs = session.expiresInSeconds * MS_PER_SECOND;
  lastGestureAt = Date.now();
  setState({ status: 'signed_in', account: session.account });
}

export const session = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  snapshot: (): SessionState => state,
  accessToken: (): string | null => accessToken,
};

export function useSession(): SessionState {
  return useSyncExternalStore(session.subscribe, session.snapshot);
}

/** A permission absent hides the menu entry and protects the route (technical §18.3); the API decides regardless. */
export function hasCentralAccess(current: SessionState = state): boolean {
  return current.account?.role === 'central_access';
}

/** Opens a session. The refresh token comes back as an `HttpOnly` cookie the page never sees. */
export async function signIn(email: string, password: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password, client: 'console' }),
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) throw signInError(response.status, body);
  adopt(sessionDto.parse(body));
}

export class SignInError extends Error {
  constructor(
    readonly code: string,
    readonly retryAfterSeconds: number | null,
  ) {
    super(code);
    this.name = 'SignInError';
  }
}

function signInError(status: number, body: unknown): SignInError {
  const problem: Record<string, unknown> = typeof body === 'object' && body !== null ? { ...body } : {};
  const code = typeof problem['code'] === 'string' ? problem['code'] : `HTTP_${status}`;
  const retryAfter = typeof problem['retryAfterSeconds'] === 'number' ? problem['retryAfterSeconds'] : null;
  return new SignInError(code, retryAfter);
}

/**
 * Exchanges the refresh cookie for a new access token. One renewal at a time,
 * in this tab and across tabs; concurrent callers share the result.
 */
export function renewSession(): Promise<boolean> {
  renewal ??= withRefreshLock(async () => {
    const response = await fetch(`${AUTH_BASE}/session`, { method: 'POST' });
    if (!response.ok) return false;
    const parsed = sessionDto.safeParse(await response.json().catch(() => null));
    if (!parsed.success) return false;
    adopt(parsed.data);
    return true;
  }).finally(() => {
    renewal = null;
  });
  return renewal;
}

function withRefreshLock(work: () => Promise<boolean>): Promise<boolean> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? locks.request(REFRESH_LOCK, () => work()).then((renewed) => renewed) : work();
}

/** On load: a refresh cookie still valid reopens the session without asking for the password. */
export async function restoreSession(): Promise<void> {
  if (!(await renewSession())) setState({ status: 'signed_out', account: null });
}

/** Ends the session on the server and in the page. */
export async function signOut(): Promise<void> {
  accessToken = null;
  setState({ status: 'signed_out', account: null });
  await fetch(`${AUTH_BASE}/session`, { method: 'DELETE' }).catch(() => undefined);
}

/** The session ended under the user's feet: keep the screen, ask for the password again. */
export function markExpired(): void {
  if (state.status !== 'signed_in') return;
  accessToken = null;
  setState({ status: 'expired', account: state.account });
}

/** Pointer and keyboard gestures only: background refetches never keep a session alive. */
export function recordGesture(): void {
  lastGestureAt = Date.now();
  if (state.status !== 'signed_in' || renewal) return;
  if (Date.now() - tokenIssuedAt > tokenLifetimeMs * PROACTIVE_RENEWAL_SHARE) void renewSession();
}

export function isIdle(now = Date.now()): boolean {
  const minutes = state.account?.sessionIdleMinutes;
  return minutes !== undefined && now - lastGestureAt >= minutes * MS_PER_MINUTE;
}

/** Past the inactivity setting: the server session is closed and the dialog asks for the password again. */
export async function expireForInactivity(): Promise<void> {
  if (state.status !== 'signed_in') return;
  markExpired();
  await fetch(`${AUTH_BASE}/session`, { method: 'DELETE' }).catch(() => undefined);
}
